import jeepneyRoutes from "../data/jeepneyRoutes.json";

export interface LatLng {
  lat: number;
  lng: number;
}

export interface JeepneyPlanStep {
  type: "walk" | "jeepney" | "transfer";
  mainLabel: string;
  label: string;
  color: string;
  distance: string;
}

export interface JeepneyCandidate {
  ok: boolean;
  type: string;
  segA?: LatLng[];
  segB?: LatLng[];
  walkTo?: { origin: LatLng; dest: LatLng };
  walkFrom?: { origin: LatLng; dest: LatLng };
  transfer?: { origin: LatLng; dest: LatLng } | null;
  totalDist?: number;
  totalTime: number;
  routeIdx: number;
  segBRouteIdx?: number;
  segBRouteName?: string;
}

interface JeepneyPlanOptions {
  getWalkDistanceMeters?: (origin: LatLng, destination: LatLng) => Promise<number>;
}

export interface JeepneyPlanResult {
  status: "ok" | "no-route";
  isDirectWalk: boolean;
  best?: JeepneyCandidate;
  steps: JeepneyPlanStep[];
  topCandidates: JeepneyCandidate[];
  fullRouteA: LatLng[];
  fullRouteB: LatLng[];
}

const BACOLOD_ROUTE_COVERAGE_MARGIN = 0.015;

export const BACOLOD_BOUNDS = jeepneyRoutes.features.reduce(
  (bounds, feature: any) => {
    feature.geometry.coordinates.forEach(([lng, lat]: number[]) => {
      if (lat > bounds.north) bounds.north = lat;
      if (lat < bounds.south) bounds.south = lat;
      if (lng > bounds.east) bounds.east = lng;
      if (lng < bounds.west) bounds.west = lng;
    });

    return bounds;
  },
  {
    north: -Infinity,
    south: Infinity,
    east: -Infinity,
    west: Infinity,
  }
);

BACOLOD_BOUNDS.north += BACOLOD_ROUTE_COVERAGE_MARGIN;
BACOLOD_BOUNDS.south -= BACOLOD_ROUTE_COVERAGE_MARGIN;
BACOLOD_BOUNDS.east += BACOLOD_ROUTE_COVERAGE_MARGIN;
BACOLOD_BOUNDS.west -= BACOLOD_ROUTE_COVERAGE_MARGIN;

export function isWithinBacolodBounds(point: LatLng) {
  return (
    point.lat <= BACOLOD_BOUNDS.north &&
    point.lat >= BACOLOD_BOUNDS.south &&
    point.lng <= BACOLOD_BOUNDS.east &&
    point.lng >= BACOLOD_BOUNDS.west
  );
}

export function normalizeCoords(geometry: any): LatLng[] {
  return geometry.coordinates.map(([lng, lat]: number[]) => ({ lat, lng }));
}

export function metersBetween(a: LatLng, b: LatLng) {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function segmentDistance(seg: LatLng[]): number {
  if (!seg || seg.length < 2) return 0;
  return seg.reduce(
    (acc, _: LatLng, i, arr) => (i === 0 ? acc : acc + metersBetween(arr[i - 1], arr[i])),
    0
  );
}

function travelTime(dist: number, mode: "walk" | "jeepney") {
  return mode === "walk" ? dist / 1 : dist / 12;
}

function getWalkStepMainLabel(distanceMeters: number) {
  return distanceMeters > 1000 ? "Walk or ride tricycle" : "Walk";
}

function getNearestPoint(routeCoords: LatLng[], point: LatLng) {
  let nearest = routeCoords[0];
  let minDist = Number.MAX_VALUE;
  let idx = 0;

  routeCoords.forEach((coord, i) => {
    const dist = metersBetween(point, coord);
    if (dist < minDist) {
      minDist = dist;
      nearest = coord;
      idx = i;
    }
  });

  return { coord: nearest, index: idx, dist: minDist };
}

function sliceSegmentOneWay(coords: LatLng[], startIdx: number, endIdx: number): LatLng[] | null {
  if (startIdx <= endIdx) return coords.slice(startIdx, endIdx + 1);
  return null;
}

const scenarioPriority: Record<string, number> = {
  scenario0: 0,
  scenario2: 1,
  scenario1: 2,
  "scenario2-walklong": 3,
  "scenario1-walklong": 4,
  scenario3: 5,
};

async function buildJeepneyCandidateSteps(
  route: JeepneyCandidate,
  getWalkDistanceMeters?: (origin: LatLng, destination: LatLng) => Promise<number>
) {
  const steps: JeepneyPlanStep[] = [];
  const getWalkDistance = async (origin: LatLng, destination: LatLng) =>
    getWalkDistanceMeters
      ? getWalkDistanceMeters(origin, destination)
      : metersBetween(origin, destination);

  if (route.walkTo) {
    const walkDistance = await getWalkDistance(route.walkTo.origin, route.walkTo.dest);
    steps.push({
      type: "walk",
      mainLabel: getWalkStepMainLabel(walkDistance),
      label: " to jeepney route",
      color: "#093843",
      distance: `${(walkDistance / 1000).toFixed(1)} km`,
    });
  }

  if (route.segA) {
    steps.push({
      type: "jeepney",
      mainLabel: "Ride",
      label: ` "${jeepneyRoutes.features[route.routeIdx]?.properties.name}" jeepney`,
      color: "#43ae91",
      distance: `${(segmentDistance(route.segA) / 1000).toFixed(1)} km`,
    });
  }

  if (route.transfer) {
    const transferDistance = await getWalkDistance(route.transfer.origin, route.transfer.dest);
    steps.push({
      type: "transfer",
      mainLabel: "Transfer",
      label: " to another jeepney",
      color: "#093843",
      distance: `${(transferDistance / 1000).toFixed(1)} km`,
    });
  }

  if (route.segB) {
    steps.push({
      type: "jeepney",
      mainLabel: "Ride",
      label: ` "${route.segBRouteName ?? jeepneyRoutes.features[route.segBRouteIdx ?? route.routeIdx]?.properties.name}" jeepney`,
      color: "#ff4570",
      distance: `${(segmentDistance(route.segB) / 1000).toFixed(1)} km`,
    });
  }

  if (route.walkFrom) {
    const walkDistance = await getWalkDistance(route.walkFrom.origin, route.walkFrom.dest);
    steps.push({
      type: "walk",
      mainLabel: getWalkStepMainLabel(walkDistance),
      label: " to destination",
      color: "#093843",
      distance: `${(walkDistance / 1000).toFixed(1)} km`,
    });
  }

  return steps;
}

export async function planJeepneyRoute(
  origin: LatLng,
  destination: LatLng,
  options: JeepneyPlanOptions = {}
): Promise<JeepneyPlanResult> {
  const directWalkDist = metersBetween(origin, destination);
  if (directWalkDist < 300) {
    const walkDistance = options.getWalkDistanceMeters
      ? await options.getWalkDistanceMeters(origin, destination)
      : directWalkDist;

    return {
      status: "ok",
      isDirectWalk: true,
      steps: [
        {
          type: "walk",
          mainLabel: getWalkStepMainLabel(walkDistance),
          label: " to destination",
          color: "#093843",
          distance: `${(walkDistance / 1000).toFixed(1)} km`,
        },
      ],
      topCandidates: [],
      fullRouteA: [],
      fullRouteB: [],
    };
  }

  const nearest = jeepneyRoutes.features
    .map((feat: any, i: number) => {
      const coords = normalizeCoords(feat.geometry);
      const nO = getNearestPoint(coords, origin);
      return { index: i, dist: nO.dist, nearestOrigin: nO };
    })
    .sort((a, b) => a.dist - b.dist)
    .slice(0, 12);

  if (nearest.length === 0) {
    return {
      status: "no-route",
      isDirectWalk: false,
      steps: [],
      topCandidates: [],
      fullRouteA: [],
      fullRouteB: [],
    };
  }

  const analyzeRoute = (routeIdx: number, nOrigin: any) => {
    const candidates: JeepneyCandidate[] = [];
    const firstRoute = jeepneyRoutes.features[routeIdx];
    const coordsA = normalizeCoords(firstRoute.geometry);

    if (nOrigin.dist > 5000) return candidates;

    let hasGoodDirect = false;

    const nDestA = getNearestPoint(coordsA, destination);
    const segA = sliceSegmentOneWay(coordsA, nOrigin.index, nDestA.index);
    if (segA) {
      const walkDist = metersBetween(nDestA.coord, destination);
      if (walkDist <= 3000) {
        const type = walkDist <= 300 ? "scenario1" : "scenario1-walklong";
        candidates.push({
          ok: true,
          type,
          segA,
          walkTo: { origin, dest: nOrigin.coord },
          walkFrom: { origin: nDestA.coord, dest: destination },
          transfer: null,
          totalDist: walkDist + nOrigin.dist + segmentDistance(segA),
          totalTime:
            travelTime(nOrigin.dist, "walk") +
            travelTime(segmentDistance(segA), "jeepney") +
            travelTime(walkDist, "walk"),
          routeIdx,
        });
        if (type === "scenario1") hasGoodDirect = true;
      }
    }

    for (let f2 = 0; f2 < jeepneyRoutes.features.length; f2++) {
      if (f2 === routeIdx) continue;
      const featB = jeepneyRoutes.features[f2];
      if (featB.properties.name !== firstRoute.properties.name) continue;

      const coordsB = normalizeCoords(featB.geometry);
      const nDestB = getNearestPoint(coordsB, destination);
      const segB = sliceSegmentOneWay(coordsB, 0, nDestB.index);
      if (segB) {
        const combined = [...coordsA.slice(nOrigin.index), ...segB];
        const rideDist = segmentDistance(combined);
        if (rideDist < 250) continue;
        const walkDist = metersBetween(nDestB.coord, destination);
        if (walkDist <= 3000) {
          const type = walkDist <= 300 ? "scenario2" : "scenario2-walklong";
          candidates.push({
            ok: true,
            type,
            segA: combined,
            walkTo: { origin, dest: nOrigin.coord },
            walkFrom: { origin: nDestB.coord, dest: destination },
            transfer: null,
            totalDist: walkDist + nOrigin.dist + segmentDistance(combined),
            totalTime:
              travelTime(nOrigin.dist, "walk") +
              travelTime(segmentDistance(combined), "jeepney") +
              travelTime(walkDist, "walk"),
            routeIdx,
          });
          if (type === "scenario2") hasGoodDirect = true;
        }
      }
    }

    if (!hasGoodDirect) {
      for (let f2 = 0; f2 < jeepneyRoutes.features.length; f2++) {
        if (f2 === routeIdx) continue;
        const featB = jeepneyRoutes.features[f2];
        if (featB.properties.name === firstRoute.properties.name) continue;

        const coordsB = normalizeCoords(featB.geometry);
        const nDestB = getNearestPoint(coordsB, destination);

        let bestTransfer = { dist: Number.MAX_VALUE, tA: null as any, tB: null as any };
        coordsA.forEach((cA, iA) => {
          coordsB.forEach((cB, iB) => {
            const d = metersBetween(cA, cB);
            if (d < bestTransfer.dist) {
              bestTransfer = { dist: d, tA: { coord: cA, index: iA }, tB: { coord: cB, index: iB } };
            }
          });
        });

        if (!bestTransfer.tA || !bestTransfer.tB || bestTransfer.dist > 600) continue;

        const routeSegA = sliceSegmentOneWay(coordsA, nOrigin.index, bestTransfer.tA.index);
        const routeSegB = sliceSegmentOneWay(coordsB, bestTransfer.tB.index, nDestB.index);
        if (!routeSegA || !routeSegB) continue;

        const rideDistA = segmentDistance(routeSegA);
        const rideDistB = segmentDistance(routeSegB);
        if (rideDistA < 250 || rideDistB < 250) continue;

        const walkDist = metersBetween(nDestB.coord, destination);
        if (walkDist > 3000) continue;

        candidates.push({
          ok: true,
          type: "scenario3",
          segA: routeSegA,
          segB: routeSegB,
          walkTo: { origin, dest: nOrigin.coord },
          walkFrom: { origin: nDestB.coord, dest: destination },
          transfer: { origin: bestTransfer.tA.coord, dest: bestTransfer.tB.coord },
          totalDist: walkDist + nOrigin.dist + bestTransfer.dist + segmentDistance(routeSegA) + segmentDistance(routeSegB),
          totalTime:
            travelTime(nOrigin.dist, "walk") +
            travelTime(segmentDistance(routeSegA), "jeepney") +
            travelTime(bestTransfer.dist, "walk") +
            travelTime(segmentDistance(routeSegB), "jeepney") +
            travelTime(walkDist, "walk"),
          routeIdx,
          segBRouteIdx: f2,
          segBRouteName: featB.properties.name,
        });
      }
    }

    return candidates;
  };

  const candidates: JeepneyCandidate[] = [];
  nearest.forEach((n) => {
    candidates.push(...analyzeRoute(n.index, n.nearestOrigin));
  });

  if (candidates.length === 0) {
    return {
      status: "no-route",
      isDirectWalk: false,
      steps: [],
      topCandidates: [],
      fullRouteA: [],
      fullRouteB: [],
    };
  }

  const sorted = candidates.sort((a, b) => {
    const timeDiff = a.totalTime - b.totalTime;
    if (Math.abs(timeDiff) > 20) return timeDiff;
    return (scenarioPriority[a.type] ?? 99) - (scenarioPriority[b.type] ?? 99);
  });

  const best = sorted[0];
  const within50s = sorted.filter((c) => c.totalTime - best.totalTime <= 50);
  const topCandidates = within50s
    .filter((candidate, index, self) => {
      const key = candidate.segB
        ? `${jeepneyRoutes.features[candidate.routeIdx]?.properties?.name}-${jeepneyRoutes.features[candidate.segBRouteIdx ?? candidate.routeIdx]?.properties?.name}`
        : `${jeepneyRoutes.features[candidate.routeIdx]?.properties?.name}`;

      return (
        index ===
        self.findIndex((c) => {
          const compareKey = c.segB
            ? `${jeepneyRoutes.features[c.routeIdx]?.properties?.name}-${jeepneyRoutes.features[c.segBRouteIdx ?? c.routeIdx]?.properties?.name}`
            : `${jeepneyRoutes.features[c.routeIdx]?.properties?.name}`;
          return compareKey === key;
        })
      );
    })
    .slice(0, 5);

  const steps = await buildJeepneyCandidateSteps(best, options.getWalkDistanceMeters);
  const fullRouteA = best.segA
    ? normalizeCoords(jeepneyRoutes.features[best.routeIdx].geometry)
    : [];
  const fullRouteB = best.segB
    ? normalizeCoords(jeepneyRoutes.features[best.segBRouteIdx ?? best.routeIdx].geometry)
    : [];

  return {
    status: "ok",
    isDirectWalk: false,
    best,
    steps,
    topCandidates,
    fullRouteA,
    fullRouteB,
  };
}
